<script lang="ts" setup>
import Column from 'primevue/column'
import DataTable from 'primevue/datatable'
import Dropdown from 'primevue/dropdown'
import InputText from 'primevue/inputtext'
import InputNumber from 'primevue/inputnumber'
import Button from 'primevue/button'
import {addYarnSetOutToProduct, getProductById, editYarnSetOutForProduct} from '@/api/products'
import { onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from 'primevue/usetoast'
import type { YarnSetOut, Weight } from '@/types/product-types'

const props = defineProps<{
  yarnSetOut: YarnSetOut[]
  fabricWeight?: Weight
  finishedWeight?: Weight
}>()

const toast = useToast()
const router = useRouter()
const productId = router.currentRoute.value.params.id as string
const saved = ref(false)
const isEditing = ref(true)

const yarnSetOutState = ref<YarnSetOut[]>([...props.yarnSetOut])

const fabricWeightState = ref<Weight>(
  props.fabricWeight ?? {
    relaxedWidth: 0,
    weightPerSqMeter: 0,
    courses: 0,
    wales: 0,
  }
)

const finishedWeightState = ref<Weight>(
  props.finishedWeight ?? {
    relaxedWidth: 0,
    weightPerSqMeter: 0,
    courses: 0,
    wales: 0,
  }
)
const getProductByIdHandler = async () => {
  const product = await getProductById(productId)

  if (product.yarnSetOuts && product.yarnSetOuts.length > 0) {
    yarnSetOutState.value = [...product.yarnSetOuts]
    fabricWeightState.value = product.yarnSetOuts[0].fabricWeight
    finishedWeightState.value = product.yarnSetOuts[0].finishedWeight
    saved.value = true
    isEditing.value = false
  } else {
    isEditing.value = true
  }
}


watch(
  () => props.yarnSetOut,
  (newVal) => {
    yarnSetOutState.value = [...newVal]
  }
)

const handleSave = async () => {
  try {
    for (const item of yarnSetOutState.value) {

        const payload ={
        cottonFeeder: item.cottonFeeder,
        woolFeeder: item.woolFeeder,
        cottonColor: item.cottonColor,
        woolColor: item.woolColor,
        fabricRelaxedWidth: fabricWeightState.value.relaxedWidth ?? 0,
        fabricWeightPerSqMeter: fabricWeightState.value.weightPerSqMeter ?? 0,
        fabricCourses: fabricWeightState.value.courses ?? 0,
        fabricWales: fabricWeightState.value.wales ?? 0,
        finishedRelaxedWidth: finishedWeightState.value.relaxedWidth ?? 0,
        finishedWeightPerSqMeter: finishedWeightState.value.weightPerSqMeter ?? 0,
        finishedCourses: finishedWeightState.value.courses ?? 0,
        finishedWales: finishedWeightState.value.wales ?? 0,
      }
      if (item.id) {
        await editYarnSetOutForProduct(productId, payload, item.id)
      } else {
        await addYarnSetOutToProduct(productId, payload)
      }
    }

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Yarn Set Out saved successfully',
      life: 5000
    })

    saved.value = true
    isEditing.value = false

  } catch (err) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Error saving Yarn Set Out',
      life: 5000
    })
    console.error('Error saving Yarn Set Out', err)
  }
}

const handleEdit = () => {
  isEditing.value = true
  saved.value = false
}
onMounted(async () => {
 await getProductByIdHandler()

})

</script>

<template>
 <div class="m-4 flex justify-end gap-2">
  <!-- Save button: shown when editing -->
  <Button
    v-if="isEditing"
    label="Save"
    class="w-1/6"
    icon="pi pi-save"
    @click="handleSave"
  />

  <!-- Edit button: shown after saved -->
  <Button
    v-else-if="saved"
    label="Edit"
    class="w-1/6"
    icon="pi pi-pencil"
    @click="handleEdit"
  />
</div>


  <DataTable
    :value="yarnSetOutState"
    paginator
    :rows="10"
    table-style="min-width: 50rem"
    striped-rows
    paginator-template="PrevPageLink CurrentPageReport NextPageLink"
    :rows-per-page-options="[10, 25]"
    current-page-report-template="Showing {first} to {last} of {totalRecords}"
  >
    <Column field="feeder" header="Feeder" style="width: 25%">
      <template #body="slotProps">
        <InputText v-model="slotProps.data.cottonFeeder" :readonly="!isEditing"/>
      </template>
    </Column>

    <Column field="yarn" header="Yarn Colour" style="width: 25%">
      <template #body="slotProps">
        <Dropdown
          v-model="slotProps.data.cottonColor"
          :options="[
            { label: 'Red', value: 'red' },
            { label: 'Blue', value: 'blue' },
            { label: 'Green', value: 'green' }
          ]"
          option-label="label"
          option-value="value"
          placeholder="Select Yarn"
          class="w-full"
          :disabled="!isEditing"
        />
      </template>
    </Column>

    <Column header="" style="width: 50%" />
  </DataTable>

   <div class="px-8 flex flex-row gap-4 items-center justify-around mb-8 mt-6">
    <div class="flex flex-col gap-4 p-5 rounded-xl border border-neutral-500">
      <p class="font-bold">Fabric Weight off M/C</p>
      <div class="flex flex-row justify-between items-center gap-4 text-sm">
        <p>Relaxed Width</p>
        <InputNumber v-model="fabricWeightState.relaxedWidth" :readonly="!isEditing" />
      </div>
      <div class="flex flex-row justify-between items-center gap-4 text-sm">
        <p>Weight Per Sq Meter</p>
        <InputNumber v-model="fabricWeightState.weightPerSqMeter" :readonly="!isEditing" />
      </div>
      <div class="flex flex-row justify-between items-center gap-4 text-sm">
        <p>Courses 3cm</p>
        <InputNumber v-model="fabricWeightState.courses" :readonly="!isEditing" />
      </div>
      <div class="flex flex-row justify-between items-center gap-4 text-sm">
        <p>Wales 3cm</p>
        <InputNumber v-model="fabricWeightState.wales" :readonly="!isEditing" />
      </div>
    </div>

    <div class="flex flex-col gap-4 p-5 rounded-xl border border-neutral-500">
      <p class="font-bold">Finished Weight</p>
      <div class="flex flex-row justify-between items-center gap-4 text-sm">
        <p>Relaxed Width</p>
        <InputNumber v-model="finishedWeightState.relaxedWidth" :readonly="!isEditing" />
      </div>
      <div class="flex flex-row justify-between items-center gap-4 text-sm">
        <p>Weight Per Sq Meter</p>
        <InputNumber v-model="finishedWeightState.weightPerSqMeter" :readonly="!isEditing" />
      </div>
      <div class="flex flex-row justify-between items-center gap-4 text-sm">
        <p>Courses 3cm</p>
        <InputNumber v-model="finishedWeightState.courses" :readonly="!isEditing" />
      </div>
      <div class="flex flex-row justify-between items-center gap-4 text-sm">
        <p>Wales 3cm</p>
        <InputNumber v-model="finishedWeightState.wales" :readonly="!isEditing" />
      </div>
    </div>
  </div>
</template>
